import React, { useCallback, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { X } from 'lucide-react';
import { RootState } from '@/store';
import { authHref } from '@/utils/authReturn';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

export interface GuestGatePrompt {
    title: string
    description: string
}

const FALLBACK_NAME = 'this musician';

/** What a visitor is told when a member-only action needs an account. Each names the person or project they were looking at. */
export const guestGatePrompts = {
    collab: (name?: string | null): GuestGatePrompt => ({
        title: `Create a free account to collab with ${name || FALLBACK_NAME}`,
        description: 'Send a request, and your email is only shared if they accept.',
    }),
    like: (name?: string | null): GuestGatePrompt => ({
        title: `Create a free account to save ${name || FALLBACK_NAME} to your liked profiles`,
        description: 'Everyone you like is kept in one place, so you can find them again.',
    }),
    apply: (projectTitle?: string | null): GuestGatePrompt => ({
        title: `Create a free account to apply to ${projectTitle ? `“${projectTitle}”` : 'this project'}`,
        description: 'Your email is only shared if they accept.',
    }),
};

interface GateState {
    open: boolean
    prompt: GuestGatePrompt
}

const CLOSED_GATE: GateState = { open: false, prompt: { title: '', description: '' } };

export interface GuestGateDialogProps extends GateState {
    onClose: () => void
    /** The control that opened the gate. Focus goes back to it on close. */
    openerRef: React.MutableRefObject<HTMLElement | null>
}

/**
 * Guard for actions that need an account. Call `requireAccount(prompt)` first in the click handler:
 * it returns true (and opens the dialog) for a visitor who is signed out, and false for a member.
 * Render `<GuestGateDialog {...gateProps} />` once next to the buttons it guards.
 */
export const useGuestGate = () => {
    const isGuest = useSelector((state: RootState) => !state.auth.user);
    const [gate, setGate] = useState<GateState>(CLOSED_GATE);
    const openerRef = useRef<HTMLElement | null>(null);

    const requireAccount = useCallback((prompt: GuestGatePrompt): boolean => {
        if (!isGuest) return false;
        // Radix only restores focus to a Dialog.Trigger, and this dialog is opened from code.
        openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setGate({ open: true, prompt });
        return true;
    }, [isGuest]);

    const onClose = useCallback(() => setGate((current) => ({ ...current, open: false })), []);

    return { isGuest, requireAccount, gateProps: { ...gate, onClose, openerRef } };
};

const PRIMARY_LINK_CLASS =
    'inline-flex h-12 w-full items-center justify-center rounded-xl bg-purple text-base font-bold text-white no-underline shadow-lg shadow-purple-light/25 transition hover:text-white hover:brightness-110';

export const GuestGateDialog: React.FC<GuestGateDialogProps> = ({ open, prompt, onClose, openerRef }) => {
    const { pathname, search, hash } = useLocation();
    const returnTo = `${pathname}${search}${hash}`;
    const primaryActionRef = useRef<HTMLAnchorElement>(null);

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            {/* Stacks above full-screen modals such as the highlights viewer. */}
            <DialogContent
                overlayClassName="z-[10050]"
                className="z-[10051] gap-5 bg-white font-sans sm:max-w-md"
                // Radix skips links when it picks the first thing to focus, which would land on Close.
                onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    primaryActionRef.current?.focus();
                }}
                onCloseAutoFocus={(event) => {
                    event.preventDefault();
                    openerRef.current?.focus();
                }}
            >
                {/* Even side padding keeps the centered text clear of the close button without shifting it. */}
                <DialogHeader className="px-4 text-center sm:text-center">
                    <DialogTitle className="text-xl font-bold leading-snug text-eerieblack">
                        {prompt.title}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-gray-600">
                        {prompt.description}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-3">
                    <Link ref={primaryActionRef} to={authHref('signup', returnTo)} className={PRIMARY_LINK_CLASS}>
                        Create free account
                    </Link>
                    <p className="text-center text-sm text-gray-600">
                        Already a member?{' '}
                        <Link
                            to={authHref('login', returnTo)}
                            className="font-bold text-purple-light underline-offset-2 hover:underline"
                        >
                            Log in
                        </Link>
                    </p>
                </div>
                <DialogClose
                    aria-label="Close"
                    className="absolute right-3 top-3 rounded-full p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-light"
                >
                    <X className="h-4 w-4" aria-hidden="true" />
                </DialogClose>
            </DialogContent>
        </Dialog>
    );
};
